import * as React from "react";
import { Alert, Snackbar } from "@mui/material";
import { ExtendedAccount } from "@/types/ExtendedAccount";
import {
  Account,
  AccountNextStep,
  AccountType,
  Person,
  ServiceProvider,
  ServiceProviderType,
  YesNo,
} from "@/types";
import { cleanedErrors } from "@/utils/Functions";
import { BooleanInput } from "@/components/atoms/BooleanInput";
import { SelectInput } from "@/components/atoms/SelectInput";
import { TextInput } from "@/components/atoms/TextInput";
import { AccountNextStepInput } from "@/components/molecules";
import { FormField } from "@/components/molecules/FormField";
import { ProbeUnknown } from "@/components/molecules/ProbeUnknown";
import { useOnChange, useOnValueChange, useUpdater } from "@/utils/callbacks";
import {
  errorsOfAddressOnly as errorsOfProperty,
  PropertyFields,
  propertyFieldsFromRecordAddressOnly,
  PropertyFieldRecord,
  propertyRecordFromAccount,
} from "./PropertyFields";

import { AccountFieldProps } from "./types/AccountFieldProps";
import { NestedErrors } from "@/utils/Types";
import {
  errorsOfResponsible,
  Responsible,
  ResponsibleForm,
  responsibleFromAccount,
} from "./ResponsibleFields/ResponsibleFields";

export type InsuranceAccount = {
  readonly accountNumber?: string;
  readonly holderNumber?: string;
  readonly accountType?: string;
  readonly accountTypeOther?: string;
  readonly vehicleMake?: string;
  readonly vehicleModel?: string;
  readonly vehicleRegistrationNumber?: string;
  readonly accountName?: string;
  readonly property?: PropertyFieldRecord;
  readonly jointClaim?: string;
  readonly accountRequest?: AccountNextStep;
  readonly responsible?: Responsible;
};

export type InsuranceErrors = NestedErrors<InsuranceAccount> | undefined;

export function errorsOf(record: InsuranceAccount): InsuranceErrors {
  return cleanedErrors({
    accountType: record?.accountType ? undefined : "required",
    accountTypeOther:
      record?.accountType !== "other" || record?.accountTypeOther ? undefined : "required",
    accountNumber: !record.accountNumber ? "required" : undefined,
    property: errorsOfProperty(record?.property),
    accountRequest: record?.accountRequest ? undefined : "required",
    responsible: errorsOfResponsible(record?.responsible, record?.accountRequest),
  });
}

export const recordFromAccount = (account?: Account): InsuranceAccount => {
  if (!account) {
    return {
      responsible: responsibleFromAccount(account),
    };
  }

  return {
    accountNumber: account.accountNumber,
    holderNumber: account.holderNumber,
    accountType: account.accountType,
    accountTypeOther: account.accountTypeOther,
    vehicleMake: account.vehicleMake,
    vehicleModel: account.vehicleModel,
    vehicleRegistrationNumber: account.vehicleRegistrationNumber,
    property: propertyRecordFromAccount(account),
    jointClaim: account.jointClaim,
    accountRequest: account.nextSteps,
    responsible: responsibleFromAccount(account),
  };
};

export type InsuranceAccountFieldsProps = AccountFieldProps;

export const InsuranceAccountFields: React.FC<InsuranceAccountFieldsProps> = ({
  properties,
  persons,
  serviceProvider,
  account,
  setBusy,
  onAccountAdded,
  updateTemplate,
  saveAccount,
  saveRecord,
  savedRecord,
}) => {
  const [record, update] = React.useState(savedRecord || recordFromAccount(account));
  const [errors, setErrors] = React.useState({} as InsuranceErrors);
  const [remoteErrors, setRemoteErrors] = React.useState(undefined as string | undefined);

  const onAccountRequestValueChange = React.useCallback(
    (accountRequest) => {
      update((s: any) => ({
        ...s,
        accountRequest,
        responsible: {},
      }));
    },
    [update]
  );

  const onJointClaimValueChanged = React.useCallback(
    (jointClaim) => {
      update((s: any) => ({
        ...s,
        jointClaim: jointClaim ? YesNo.Yes : YesNo.No,
      }));
    },
    [update]
  );

  const updateProperty = useUpdater(update, "property");
  const updateAccountHolder = useUpdater(update, "responsible");
  const onAccountTypeValueChange = useOnValueChange(update, "accountType");
  const onChange = useOnChange(update);

  React.useEffect(() => {
    if (!updateTemplate) {
      return;
    }

    updateTemplate({
      // onNextLabel: "Continue",
      onNext: !serviceProvider?.serviceProviderType
        ? undefined
        : () => {
            const errors = errorsOf(record);

            if (errors) {
              setErrors(errors);
              if (saveRecord) {
                saveRecord({ source: record });
              }
              return;
            }

            const responsible = {
              id: record.responsible?.responsibleId || undefined,
              details: record.responsible?.responsible || undefined,
              bankAccount: record.responsible?.responsibleBankAccount || undefined,
            };

            const data = {
              id: account?.id,
              serviceProvider: {
                id: serviceProvider.serviceProviderId,
                companyName: serviceProvider.customProviderName,
                serviceProviderType: serviceProvider.serviceProviderType,
              },
              accountNumber: record.accountNumber,
              accountType: record.accountType,
              accountTypeOther: record.accountTypeOther,
              accountName: record.accountName,
              ...propertyFieldsFromRecordAddressOnly(record.property),
              vehicleMake: record.vehicleMake,
              vehicleModel: record.vehicleModel,
              vehicleRegistrationNumber: record.vehicleRegistrationNumber,
              nextStep: record.accountRequest,
              // documentIds,
              holderNumber: record.holderNumber,
              jointClaim: record.jointClaim,
              responsible,
            };

            if (saveRecord) {
              saveRecord({ source: record, target: data });
              return { response: {} };
            }

            setBusy(true);

            return saveAccount({
              sector: ServiceProviderType.Insurance,
              record: data,
            }).then(
              (
                response: {
                  data: {
                    account: ExtendedAccount;
                    newServiceProvider?: ServiceProvider;
                    newPerson?: Person;
                  };
                } & { error: Error }
              ) => {
                setBusy(false);
                if (response.error) {
                  setRemoteErrors(response.error.message);
                  return;
                }
                if (response.data) {
                  onAccountAdded(response.data.account, {
                    newServiceProvider: response.data.newServiceProvider,
                    newPerson: response.data.newPerson,
                  });
                  return;
                }
              },
              (err: Error) => {
                console.warn({ err });
                setBusy(false);
                setRemoteErrors("Operation failed. Please try again or contact customer support.");
              }
            );
          },
    });
  }, [updateTemplate, serviceProvider, account, onAccountAdded, record, setBusy, saveAccount]);

  return (
    <>
      <FormField label="Policy number">
        <TextInput
          name="accountNumber"
          value={record.accountNumber || ""}
          onChange={onChange}
          error={errors?.accountNumber}
          placeholder="00000000"
        />
      </FormField>

      <FormField>
        <ProbeUnknown
          checked={record.accountNumber === "00000000"}
          label={"I don't know the policy Number"}
          update={update}
          defaultValue={{ accountNumber: "00000000" }}
          emptyValue={{ accountNumber: "" }}
        />
      </FormField>

      <FormField halfWidth label="Customer reference">
        <TextInput
          name="holderNumber"
          value={record.holderNumber || ""}
          onChange={onChange}
          error={errors?.holderNumber}
          placeholder="000000"
        />
      </FormField>

      <FormField halfWidth label="Policy type">
        <SelectInput
          name="accountType"
          value={record.accountType || ""}
          onValueChange={onAccountTypeValueChange}
          error={errors?.accountType}
          options={accountTypeOptions}
        />
        {record.accountType === AccountType.Other && (
          <TextInput
            name={"accountTypeOther"}
            value={record.accountTypeOther || ""}
            error={errors?.accountTypeOther}
            onChange={onChange}
          />
        )}
      </FormField>

      <FormField halfWidth label="Policy name/Scheme type">
        <TextInput name="accountName" value={record.accountName || ""} onChange={onChange} />
      </FormField>

      {(record.accountType === AccountType.Vehicle ||
        record.accountType === AccountType.BreakdownCover) && (
        <>
          <FormField halfWidth label="Vehicle make">
            <TextInput
              name="vehicleMake"
              value={record.vehicleMake || ""}
              onChange={onChange}
              error={errors?.vehicleMake}
            />
          </FormField>

          <FormField halfWidth label="Vehicle model">
            <TextInput
              name="vehicleModel"
              value={record.vehicleModel || ""}
              onChange={onChange}
              error={errors?.vehicleModel}
            />
          </FormField>

          <FormField halfWidth label="Vehicle registration number">
            <TextInput
              name="vehicleRegistrationNumber"
              value={record.vehicleRegistrationNumber || ""}
              onChange={onChange}
              error={errors?.vehicleRegistrationNumber}
            />
          </FormField>
        </>
      )}

      <PropertyFields
        serviceProvider={serviceProvider}
        addressOnly
        label="What is the address associated with this policy?"
        serviceProviderId={serviceProvider.serviceProviderId}
        persons={persons}
        properties={properties}
        record={record.property || {}}
        errors={errors?.property}
        update={updateProperty}
      />

      <FormField>
        <BooleanInput
          label="This is a joint policy"
          value={record.jointClaim === YesNo.Yes}
          onValueChange={onJointClaimValueChanged}
        />
      </FormField>

      <FormField label="Next step">
        <AccountNextStepInput
          onValueChange={onAccountRequestValueChange}
          value={record.accountRequest || ""}
          serviceProviderType={ServiceProviderType.Insurance}
          error={errors?.accountRequest}
        />
      </FormField>

      {record.responsible && record.accountRequest && (
        <ResponsibleForm
          serviceProvider={serviceProvider}
          persons={persons}
          labelOverride={
            record.accountRequest === AccountNextStep.UpdateAccountHolder
              ? "Who will take over the policy?"
              : undefined
          }
          nextStep={record.accountRequest}
          record={record.responsible}
          errors={errors?.responsible}
          update={updateAccountHolder}
        />
      )}

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteErrors}
        autoHideDuration={6000}
        onClose={() => setRemoteErrors(undefined)}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity="error"
          onClose={() => setRemoteErrors(undefined)}
        >
          {remoteErrors}
        </Alert>
      </Snackbar>
    </>
  );
};

const accountTypeOptions = [
  { value: AccountType.Vehicle, label: "Vehicle" },
  { value: AccountType.BreakdownCover, label: "Breakdown cover" },
  { value: AccountType.Home, label: "Home" },
  { value: AccountType.Health, label: "Health" },
  { value: AccountType.Travel, label: "Travel" },
  { value: AccountType.Business, label: "Business" },
  { value: AccountType.Life, label: "Life" },
  { value: AccountType.Disability, label: "Disability" },
  { value: AccountType.Pet, label: "Pet" },
  { value: AccountType.Other, label: "Other" },
];
